import { useEffect, useMemo, useState } from 'react';
import { FieldValues, UseFieldArrayAppend } from 'react-hook-form';
import { uuid4 } from '@sentry/core';

import { Spin } from 'antd';
import { IPrimeIndividual, useLazyIndividualEntitiesQuery } from 'api/RegistryApiSlice';
import { SearchInput } from 'components';
import dayjs from 'dayjs';
import { useDebounce } from 'use-debounce';
import { normaliseCountryValue } from 'utils/countries';
import { SelectedIndividual } from './SelectedIndividual';

interface LookupIndividualContentProps {
  append: UseFieldArrayAppend<FieldValues, 'individuals'>;
  disabled: boolean;
}

export const LookupIndividualContent = ({ disabled, append }: LookupIndividualContentProps) => {
  const [searchIndividual, { data: response, isLoading }] = useLazyIndividualEntitiesQuery();
  const data = useMemo(() => {
    return (
      response?.data?.map((d) => ({
        ...d,
        id: uuid4(),
      })) ?? []
    );
  }, [response]);

  const [name, setName] = useState<string>('');

  const [debouncedQuery] = useDebounce(name, 800);

  const searchOptions = isLoading
    ? [
        {
          value: 'loading',
          label: (
            <div className="flex items-center justify-center">
              <Spin size="small" />
              <span className="ml-2">Loading...</span>
            </div>
          ),
        },
      ]
    : name && data?.length > 0
      ? data.map((user: IPrimeIndividual) => {
          const {
            id,
            givenName1,
            givenName2,
            givenName3,
            surname,
            email,
            mobile,
            dob,
            address: { building, street, suburb, state, postCode, country },
          } = user;

          const address = [building, street, suburb, state, postCode, country]
            .filter((addressComponent) => addressComponent)
            .join(', ');

          const individual = {
            id,
            address,
            firstName: [givenName1, givenName2, givenName3].join(' '),
            lastName: surname,
            email,
            mobilePhone: mobile,
            dateOfBirth: dob?.toString(),
          };

          return {
            value: id,
            label: <SelectedIndividual user={individual} searchQuery={name} areOptionsVisible />,
          };
        })
      : name && data?.length === 0
        ? [
            {
              value: 'no-result',
              label: <div className="text-gray-500 font-medium">No results found.</div>,
            },
          ]
        : [];

  useEffect(() => {
    const fetchData = async () => {
      if (name) {
        try {
          await searchIndividual(name);
        } catch {
          //
        }
      }
    };

    fetchData();
  }, [debouncedQuery, searchIndividual]);

  const handleSelect = (value: string) => {
    if (value === 'no-result') return;
    const selectedItem = data?.find((item) => item.id === value);
    if (selectedItem) {
      const {
        givenName1: firstName,
        givenName2,
        givenName3,
        surname: lastName,
        dob,
        email,
        mobile: mobilePhone,
        address: { country, postCode: postcode, state, street: streetName, suburb },
      } = selectedItem;

      append({
        firstName,
        lastName,
        middleName: (givenName2 + ' ' + givenName3).trim(),
        dateOfBirth: dayjs(dob),
        email,
        mobilePhone,
        relationship: '',
        country: normaliseCountryValue(country),
        postcode,
        state,
        streetName,
        streetNumber: '',
        streetType: '',
        suburb,
      });
    }
  };

  return (
    <>
      <span className="text-gray-600">
        Load details from an existing individual in Prime Compliance.
      </span>
      <SearchInput
        placeholder="Name"
        onSearch={(value: string) => setName(value)}
        onSelect={handleSelect}
        value={name}
        options={searchOptions}
        disabled={disabled}
      />
    </>
  );
};
